import Box from '@/Atoms/Box';
import React, {useEffect, useRef, useState} from 'react';
import AntDesign from 'react-native-vector-icons/AntDesign';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import Spacer from '@/Atoms/Spacer';
import CreateNewStudy from '@/Screens/StudyGameList/Component/CreateNewStudy';
import {
  View,
  ScrollView,
  GestureResponderEvent,
  TouchableOpacity,
  Text,
} from 'react-native';
import {styles} from './styles';
import Ionicons from 'react-native-vector-icons/Ionicons';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import useDeleteChapter from '@/Hooks/ManageStudyParticipantsApi/useDeleteChapter';
import useReorderChapters from '@/Hooks/CreateNewChapterApi/useReorderChapters';
import useBulkImportPgn from '@/Hooks/CreateNewChapterApi/useBulkImportPgn';
import useBulkImportPgnStatus from '@/Hooks/CreateNewChapterApi/useBulkImportPgnStatus';
import SlideModal from '@/Atoms/SlideModal';
import {useToast} from '@/toast/useToast';
import Dropzone from 'react-dropzone';
import ChapterSettingsPanel from '../ChapterSettingsPanel';

// Small hoverable icon button used for the up/down/settings/delete row
// actions - a plain TouchableOpacity around the icon so there's a real hit
// target and a hover tint, instead of a bare icon glyph floating on the row.
function ChapterActionButton({
  iconName,
  color,
  hoverColor,
  hoverBackground,
  disabled,
  onPress,
}: {
  iconName: string;
  color: string;
  hoverColor: string;
  hoverBackground: string;
  disabled?: boolean;
  onPress: (e: GestureResponderEvent) => void;
}) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <TouchableOpacity
      disabled={disabled}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onPress={onPress}
      style={[
        styles.actionButton,
        isHovered && !disabled && {backgroundColor: hoverBackground},
      ]}>
      <AntDesign
        name={iconName}
        size={14}
        color={
          disabled
            ? CCColors.Workbook.BeigeMuted
            : isHovered
            ? hoverColor
            : color
        }
      />
    </TouchableOpacity>
  );
}

type ChapterRowProps = {
  chapter: any;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  isSelected: boolean;
  isOwner: boolean;
  onSelect: () => void;
  onMoveUp: (e: GestureResponderEvent) => void;
  onMoveDown: (e: GestureResponderEvent) => void;
  onOpenSettings: (e: GestureResponderEvent) => void;
  onDelete: (e: GestureResponderEvent) => void;
};

// Extracted so each row owns its own hover state (react-native-web's
// onHoverIn/onHoverOut + local state is this codebase's usual hover
// pattern - that only works per-instance, not from inside a `.map()`).
function ChapterRow({
  chapter,
  index,
  isFirst,
  isLast,
  isSelected,
  isOwner,
  onSelect,
  onMoveUp,
  onMoveDown,
  onOpenSettings,
  onDelete,
}: ChapterRowProps) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <Box
      style={[
        styles.participantsName,
        isSelected ? styles.selectedName : styles.unselectedName,
        isHovered && !isSelected && styles.hoveredName,
      ]}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onPress={onSelect}>
      <Box style={styles.rowLeading}>
        <Box
          style={[
            styles.chapterAvatar,
            isSelected && styles.chapterAvatarSelected,
          ]}>
          <Text
            style={[
              styles.chapterAvatarText,
              isSelected && styles.chapterAvatarTextSelected,
            ]}>
            {index + 1}
          </Text>
        </Box>
        <Text
          numberOfLines={1}
          style={{
            flex: 1,
            fontSize: 13,
            fontWeight: isSelected ? '500' : '400',
            color: isSelected
              ? CCColors.TextColor.Dark
              : CCColors.TextColor.Primary,
          }}>
          {chapter?.name}
        </Text>
      </Box>
      {isSelected && !isOwner && (
        <Ionicons
          name="chevron-forward"
          size={14}
          color={CCColors.Workbook.GoldText}
          style={{opacity: 0.5}}
        />
      )}
      {isSelected && isOwner && (
        <Box style={styles.actionsRow}>
          <ChapterActionButton
            iconName="up"
            color={CCColors.TextColor.Primary}
            hoverColor={CCColors.Workbook.GoldText}
            hoverBackground="rgba(250,207,71,0.08)"
            disabled={isFirst}
            onPress={onMoveUp}
          />
          <ChapterActionButton
            iconName="down"
            color={CCColors.TextColor.Primary}
            hoverColor={CCColors.Workbook.GoldText}
            hoverBackground="rgba(250,207,71,0.08)"
            disabled={isLast}
            onPress={onMoveDown}
          />
          <ChapterActionButton
            iconName="setting"
            color={CCColors.TextColor.Primary}
            hoverColor={CCColors.Workbook.GoldText}
            hoverBackground="rgba(250,207,71,0.08)"
            onPress={onOpenSettings}
          />
          <ChapterActionButton
            iconName="delete"
            color={CCColors.Error}
            hoverColor={CCColors.Error}
            hoverBackground={updateColorWithOpacity(
              CCColors.Workbook.DangerRed,
              0.12,
            )}
            onPress={onDelete}
          />
        </Box>
      )}
    </Box>
  );
}

type ChaptersProps = {
  chaptersList: any[];
  gameId: string;
  setLoading: (loading: boolean) => void;
  refetchStudy: () => void;
  isOwner: boolean;
  selectedChapter: any;
  setSelectedChapter: (chapter: any) => void;
  setOpenRemoveConfimation: (data: any) => void;
  setNewChapterCreated: (created: boolean) => void;
  setBoardOrientation: (orientation: 'white' | 'black') => void;
  // Desktop passes this to switch Study's engine-column Settings tab in
  // instead of opening chapterSettingsModalVisible below (see
  // ChapterSettingsPanel). Mobile doesn't have that tabbed column, so it
  // leaves this unset and falls back to the local modal.
  onOpenChapterSettings?: () => void;
};

function Chapters({
  chaptersList,
  gameId,
  setLoading,
  refetchStudy,
  isOwner,
  selectedChapter,
  setSelectedChapter,
  setOpenRemoveConfimation,
  setNewChapterCreated,
  setBoardOrientation,
  onOpenChapterSettings,
}: ChaptersProps) {
  const [chapterSettingsModalVisible, setChapterSettingsModalVisible] =
    useState(false);
  const [isAddHovered, setIsAddHovered] = useState(false);
  const [isImportHovered, setIsImportHovered] = useState(false);

  const handleParticipantsSuccess = () => {
    setLoading(false);
    refetchStudy();
  };

  const handleParticipantsError = () => {
    setLoading(false);
  };

  const {mutate: deleteChapter} = useDeleteChapter(
    handleParticipantsSuccess,
    handleParticipantsError,
  );

  const showToast = useToast();
  const {mutate: reorderChapters} = useReorderChapters(
    () => refetchStudy(),
    () => showToast('Failed to reorder chapters', {type: 'Error'}),
  );

  const handleMoveChapter = (chapter: any, direction: 'up' | 'down') => {
    const currentIndex = chaptersList.findIndex(c => c?.id === chapter?.id);
    const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (currentIndex < 0 || swapIndex < 0 || swapIndex >= chaptersList.length) {
      return;
    }

    const reordered = [...chaptersList];
    [reordered[currentIndex], reordered[swapIndex]] = [
      reordered[swapIndex],
      reordered[currentIndex],
    ];
    reorderChapters({gameId, chapterIds: reordered.map(c => c?.id)});
  };

  const [bulkImportModalVisible, setBulkImportModalVisible] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState<File | null>(null);
  const [bulkImportProgress, setBulkImportProgress] = useState<{
    imported: number;
    total: number;
  }>({imported: 0, total: 0});
  const [isBulkImporting, setIsBulkImporting] = useState(false);

  // Tracks the pending recursive poll timeout so it can be cancelled on
  // unmount, and the current import id so the poll callback (whose closure
  // is fixed at mutation-creation time) always reads the latest value
  // instead of a stale one.
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bulkImportIdRef = useRef<string | null>(null);

  useEffect(
    () => () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    },
    [],
  );

  const {mutate: pollBulkImportStatus} = useBulkImportPgnStatus(
    (response: any) => {
      if (response?.status === 'COMPLETED') {
        setIsBulkImporting(false);
        setBulkImportProgress({imported: 0, total: 0});
        setBulkImportModalVisible(false);
        setBulkImportFile(null);
        bulkImportIdRef.current = null;
        refetchStudy();
        showToast('PGN import complete', {type: 'Success'});
      } else if (
        response?.status === 'IN_PROGRESS' &&
        bulkImportIdRef.current
      ) {
        const imported = response?.data?.imported_chapters || 0;
        const total = response?.data?.total_chapters || 0;
        setBulkImportProgress({imported, total});
        pollTimeoutRef.current = setTimeout(
          () =>
            pollBulkImportStatus({gameId, importId: bulkImportIdRef.current}),
          1000,
        );
      }
    },
    () => {
      setIsBulkImporting(false);
      setBulkImportProgress({imported: 0, total: 0});
      showToast('Failed to check import progress', {type: 'Error'});
    },
  );

  const {mutate: bulkImportPgn} = useBulkImportPgn(
    (response: any) => {
      const importId = response?.data?.import_id;
      if (importId) {
        bulkImportIdRef.current = importId;
        setIsBulkImporting(true);
        setBulkImportProgress({imported: 0, total: 0});
        pollBulkImportStatus({gameId, importId});
      }
    },
    () => {
      setIsBulkImporting(false);
      showToast('Failed to start PGN import', {type: 'Error'});
    },
  );

  const handleStartBulkImport = () => {
    if (!bulkImportFile) {
      return;
    }
    bulkImportPgn({gameId, file: bulkImportFile});
  };

  return (
    <>
      <Spacer spacing={10} />
      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={{paddingBottom: 20}}
        showsVerticalScrollIndicator={true}>
        <Box>
          {chaptersList?.map((chapter, chapterIndex) => {
            const isSelected = selectedChapter?.id === chapter?.id;
            return (
              <ChapterRow
                key={chapter?.id}
                chapter={chapter}
                index={chapterIndex}
                isFirst={chapterIndex === 0}
                isLast={chapterIndex === chaptersList.length - 1}
                isSelected={isSelected}
                isOwner={isOwner}
                onSelect={() => {
                  setSelectedChapter(chapter);
                  setNewChapterCreated(false);
                  setBoardOrientation(
                    chapter?.orientation === 'W' ? 'white' : 'black',
                  );
                }}
                onMoveUp={e => {
                  e.stopPropagation();
                  handleMoveChapter(chapter, 'up');
                }}
                onMoveDown={e => {
                  e.stopPropagation();
                  handleMoveChapter(chapter, 'down');
                }}
                onOpenSettings={e => {
                  e.stopPropagation();
                  if (onOpenChapterSettings) {
                    onOpenChapterSettings();
                  } else {
                    setChapterSettingsModalVisible(true);
                  }
                }}
                onDelete={e => {
                  e.stopPropagation();
                  setOpenRemoveConfimation({
                    flag: true,
                    title: 'Capture this Chapter?',
                    bodyTxt: `"${chapter?.name}" will be swept off the board for everyone.`,
                    piece: 'chess-rook',
                    pieceRotation: 24,
                    confirmLabel: 'Yes, Delete',
                    bullets: [
                      {
                        icon: 'source-branch',
                        text: 'Every move and variation in this chapter is deleted',
                      },
                      {
                        icon: 'comment-text-outline',
                        text: 'Annotations, hints and comments go with it',
                      },
                    ],
                    callbackFunc: async () =>
                      await deleteChapter({
                        studySlug: gameId,
                        chapterId: chapter?.id,
                      }),
                  });
                }}
              />
            );
          })}
        </Box>
      </ScrollView>
      {isOwner && (
        <View style={styles.stickyButtonWrapper}>
          <CreateNewStudy
            overrideButton={(
              onClickFunc: ((event: GestureResponderEvent) => void) | undefined,
            ) => (
              <TouchableOpacity
                style={[
                  styles.addNewBox,
                  isAddHovered && {
                    borderColor: '#FACF47',
                    backgroundColor: '#FFFDF6',
                  },
                ]}
                onHoverIn={() => setIsAddHovered(true)}
                onHoverOut={() => setIsAddHovered(false)}
                onPress={onClickFunc}>
                <MaterialCommunityIcons
                  name="plus-circle-outline"
                  size={15}
                  color={isAddHovered ? CCColors.Workbook.GoldText : '#2C1A11'}
                />
                <Text
                  style={{
                    fontSize: 13,
                    fontWeight: '600',
                    color: isAddHovered
                      ? CCColors.Workbook.GoldText
                      : '#2C1A11',
                    marginLeft: 6,
                  }}>
                  Add Chapter
                </Text>
              </TouchableOpacity>
            )}
            createChapterOnly
            successCallback={refetchStudy}
            studySlug={gameId}
            setNewChapterCreated={setNewChapterCreated}
          />
          <Spacer spacing={8} />
          <TouchableOpacity
            style={[
              styles.addNewBox,
              isImportHovered && {
                borderColor: '#FACF47',
                backgroundColor: '#FFFDF6',
              },
            ]}
            onHoverIn={() => setIsImportHovered(true)}
            onHoverOut={() => setIsImportHovered(false)}
            onPress={() => setBulkImportModalVisible(true)}>
            <MaterialCommunityIcons
              name="file-upload-outline"
              size={15}
              color={isImportHovered ? CCColors.Workbook.GoldText : '#2C1A11'}
            />
            <Text
              style={{
                fontSize: 13,
                fontWeight: '600',
                color: isImportHovered ? CCColors.Workbook.GoldText : '#2C1A11',
                marginLeft: 6,
              }}>
              Import PGN (multi-game)
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Mobile-only fallback - desktop uses onOpenChapterSettings to switch
          Study's engine-column Settings tab instead of this modal. */}
      {!onOpenChapterSettings && (
        <SlideModal
          visible={chapterSettingsModalVisible}
          onClose={() => setChapterSettingsModalVisible(false)}
          type="center"
          showHeader={false}
          contentPadding={0}
          customStyle={{
            borderRadius: 20,
            overflow: 'hidden',
            backgroundColor: '#FFFFFF',
            width: 450,
            maxWidth: '92%',
            alignSelf: 'center',
            borderWidth: 0,
            shadowColor: '#000',
            shadowOffset: {width: 0, height: 10},
            shadowOpacity: 0.08,
            shadowRadius: 20,
            elevation: 5,
          }}>
          {/* Header Block */}
          <View
            style={{
              backgroundColor: '#2C1A11',
              paddingHorizontal: 24,
              paddingVertical: 18,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative',
            }}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
                flex: 1,
                marginRight: 24,
              }}>
              <View
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 8,
                  backgroundColor: '#402C1F',
                  borderWidth: 1,
                  borderColor: '#B58E58',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <MaterialCommunityIcons
                  name="chess-rook"
                  size={18}
                  color="#FFFFFF"
                />
              </View>
              <View style={{flex: 1}}>
                <Text
                  style={{
                    fontSize: 15,
                    fontWeight: 'bold',
                    color: '#FFFFFF',
                    letterSpacing: -0.2,
                  }}>
                  Chapter Settings
                </Text>
                {selectedChapter?.name ? (
                  <Text style={{fontSize: 11, color: '#A39081', marginTop: 1}}>
                    {selectedChapter.name}
                  </Text>
                ) : null}
              </View>
            </View>

            <TouchableOpacity
              onPress={() => setChapterSettingsModalVisible(false)}
              activeOpacity={0.8}
              style={{
                width: 28,
                height: 28,
                borderRadius: 14,
                backgroundColor: 'rgba(255,255,255,0.08)',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Ionicons name="close-outline" size={16} color="#FFFFFF" />
            </TouchableOpacity>
          </View>

          <View style={{padding: 24}}>
            <ChapterSettingsPanel
              chapter={selectedChapter}
              gameId={gameId}
              setLoading={setLoading}
              refetchStudy={refetchStudy}
              setSelectedChapter={setSelectedChapter}
            />
          </View>
        </SlideModal>
      )}

      <SlideModal
        visible={bulkImportModalVisible}
        onClose={() => {
          if (isBulkImporting) {
            return;
          }
          setBulkImportModalVisible(false);
          setBulkImportFile(null);
        }}
        type="center"
        showHeader={false}
        contentPadding={0}
        customStyle={{
          borderRadius: 20,
          overflow: 'hidden',
          backgroundColor: '#FFFFFF',
          width: 450,
          maxWidth: '92%',
          alignSelf: 'center',
          borderWidth: 0,
          shadowColor: '#000',
          shadowOffset: {width: 0, height: 10},
          shadowOpacity: 0.08,
          shadowRadius: 20,
          elevation: 5,
        }}>
        {/* Header Block */}
        <View
          style={{
            backgroundColor: '#2C1A11',
            paddingHorizontal: 24,
            paddingVertical: 18,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
          }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
              flex: 1,
              marginRight: 24,
            }}>
            <View
              style={{
                width: 38,
                height: 38,
                borderRadius: 8,
                backgroundColor: '#402C1F',
                borderWidth: 1,
                borderColor: '#B58E58',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <MaterialCommunityIcons
                name="chess-rook"
                size={18}
                color="#FFFFFF"
              />
            </View>
            <View style={{flex: 1}}>
              <Text
                style={{
                  fontSize: 15,
                  fontWeight: 'bold',
                  color: '#FFFFFF',
                  letterSpacing: -0.2,
                }}>
                Import PGN
              </Text>
              <Text style={{fontSize: 11, color: '#A39081', marginTop: 1}}>
                Each game in the file becomes its own chapter, appended after
                your existing ones.
              </Text>
            </View>
          </View>

          {/* Close button */}
          {!isBulkImporting && (
            <TouchableOpacity
              onPress={() => {
                setBulkImportModalVisible(false);
                setBulkImportFile(null);
              }}
              activeOpacity={0.8}
              style={{
                width: 28,
                height: 28,
                borderRadius: 14,
                backgroundColor: 'rgba(255,255,255,0.08)',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Ionicons name="close-outline" size={16} color="#FFFFFF" />
            </TouchableOpacity>
          )}
        </View>

        {/* Body container */}
        <View style={{padding: 24, gap: 16}}>
          {isBulkImporting ? (
            <View style={{alignItems: 'center', padding: 16, gap: 14}}>
              {/* Progress squares */}
              <View style={{flexDirection: 'row', gap: 4}}>
                {Array.from({length: 8}).map((_, i) => {
                  const ratio = bulkImportProgress.total
                    ? bulkImportProgress.imported / bulkImportProgress.total
                    : 0;
                  const filled = ratio * 8 > i;
                  return (
                    <View
                      key={i}
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 5,
                        backgroundColor: filled
                          ? i % 2 === 0
                            ? CCColors.TextColor.Primary
                            : CCColors.Workbook.Brown
                          : updateColorWithOpacity(
                              CCColors.TextColor.Primary,
                              0.08,
                            ),
                        borderWidth: 1,
                        borderColor: filled
                          ? CCColors.TextColor.Primary
                          : updateColorWithOpacity(
                              CCColors.TextColor.Primary,
                              0.12,
                            ),
                      }}
                    />
                  );
                })}
              </View>
              <Text
                style={{
                  fontWeight: '600',
                  color: CCColors.TextColor.Primary,
                  fontSize: 13,
                }}>
                {bulkImportProgress.total
                  ? `Setting up chapter ${Math.min(
                      bulkImportProgress.imported + 1,
                      bulkImportProgress.total,
                    )} of ${bulkImportProgress.total}…`
                  : 'Reading your games…'}
              </Text>
            </View>
          ) : (
            <Dropzone
              accept={{'application/x-chess-pgn': ['.pgn']}}
              maxFiles={1}
              onDrop={(acceptedFiles: File[]) =>
                setBulkImportFile(acceptedFiles[0] ?? null)
              }>
              {({getRootProps, getInputProps}) => (
                // @ts-ignore
                <div
                  {...getRootProps()}
                  style={{
                    border: bulkImportFile
                      ? `2px dashed ${CCColors.Green}`
                      : `2px dashed ${updateColorWithOpacity(
                          CCColors.TextColor.Primary,
                          0.25,
                        )}`,
                    borderRadius: 12,
                    padding: 24,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: bulkImportFile
                      ? updateColorWithOpacity(CCColors.Green, 0.06)
                      : CCColors.White,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 10,
                  }}>
                  {/* @ts-ignore */}
                  <input {...getInputProps()} />
                  {/* Checkerboard */}
                  <View
                    style={{
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      width: 56,
                      height: 28,
                      borderRadius: 4,
                      overflow: 'hidden',
                      opacity: 0.8,
                    }}>
                    {Array.from({length: 8}).map((_, i) => {
                      const r = Math.floor(i / 4);
                      const c = i % 4;
                      return (
                        <View
                          key={i}
                          style={{
                            width: 14,
                            height: 14,
                            backgroundColor:
                              (r + c) % 2 === 0
                                ? CCColors.Workbook.Beige
                                : CCColors.TextColor.Primary,
                          }}
                        />
                      );
                    })}
                  </View>
                  <Text
                    style={{
                      fontSize: 13,
                      fontWeight: bulkImportFile ? '600' : '400',
                      color: bulkImportFile
                        ? CCColors.Green
                        : CCColors.Workbook.Brown,
                    }}>
                    {bulkImportFile
                      ? bulkImportFile.name
                      : 'Drop your .pgn here, or click to browse'}
                  </Text>
                  {!bulkImportFile && (
                    <Text style={{fontSize: 11, color: CCColors.Workbook.Grey}}>
                      Multi-game files welcome — every game gets a chapter
                    </Text>
                  )}
                </div>
              )}
            </Dropzone>
          )}
        </View>

        {/* Footer */}
        {!isBulkImporting && (
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'flex-end',
              gap: 10,
              paddingHorizontal: 24,
              paddingBottom: 20,
              paddingTop: 14,
              borderTopWidth: 1,
              borderColor: '#F5F5F5',
            }}>
            <TouchableOpacity
              onPress={() => {
                setBulkImportModalVisible(false);
                setBulkImportFile(null);
              }}
              activeOpacity={0.8}
              style={{
                borderWidth: 1.5,
                borderColor: '#CCCCCC',
                backgroundColor: '#FFFFFF',
                borderRadius: 100,
                paddingHorizontal: 22,
                paddingVertical: 8,
              }}>
              <Text
                style={{fontSize: 12, fontWeight: 'bold', color: '#2E1B10'}}>
                Cancel
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              disabled={!bulkImportFile}
              onPress={handleStartBulkImport}
              activeOpacity={0.8}
              style={{
                backgroundColor: bulkImportFile ? '#FACF47' : '#F5F5F5',
                borderRadius: 100,
                paddingHorizontal: 24,
                paddingVertical: 8,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}>
              <MaterialCommunityIcons
                name="file-upload-outline"
                size={14}
                color={bulkImportFile ? '#1E110A' : '#B0B0B0'}
              />
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: 'bold',
                  color: bulkImportFile ? '#1E110A' : '#B0B0B0',
                }}>
                Start Import
              </Text>
            </TouchableOpacity>
          </View>
        )}
      </SlideModal>
    </>
  );
}

export default Chapters;
